import { api } from './client'
import { Order, Payment, CalcPreview, ClientCreditProfile } from '../types'

export async function getOrderClientProfile(orderId: number): Promise<ClientCreditProfile> {
  const res = await api.get(`/api/v1/orders/${orderId}/client-profile`)
  return res.data
}

export async function calcPreviewByPrice(data: {
  product_price: number; down_payment: number; months: number; discount_pct?: number
}): Promise<CalcPreview> {
  const res = await api.post('/api/v1/orders/calculator', { ...data, discount_pct: data.discount_pct ?? 0 })
  return res.data
}

export async function createOrder(data: {
  product_id: number; down_payment: number; months: number
}): Promise<Order> {
  const res = await api.post('/api/v1/orders', data)
  return res.data
}

export async function createCustomOrder(data: {
  product_name: string; product_price: number; down_payment: number; months: number; comment?: string
}): Promise<Order> {
  const res = await api.post('/api/v1/orders/custom', data)
  return res.data
}

export async function adminCreateOrder(data: {
  user_id: number; product_name: string; product_price: number; down_payment: number; months: number; comment?: string
}): Promise<Order> {
  const res = await api.post('/api/v1/orders/admin-create', data)
  return res.data
}

export async function getOrders(params?: {
  status?: string; offset?: number; limit?: number
}): Promise<{ items: Order[]; total: number }> {
  const res = await api.get('/api/v1/orders', { params })
  return res.data
}

export async function getOrder(id: number): Promise<Order> {
  const res = await api.get(`/api/v1/orders/${id}`)
  return res.data
}

export async function approveOrder(id: number, data: {
  custom_rate?: number; note?: string
}): Promise<Order> {
  const res = await api.post(`/api/v1/orders/${id}/approve`, data)
  return res.data
}

export async function rejectOrder(id: number, reason: string): Promise<Order> {
  const res = await api.post(`/api/v1/orders/${id}/reject`, { reason })
  return res.data
}

export async function updateOrder(id: number, data: {
  product_name?: string; product_price?: number; down_payment?: number;
  months?: number; interest_rate?: number; discount_pct?: number;
  first_payment_date?: string; manager_note?: string;
}): Promise<Order> {
  const res = await api.patch(`/api/v1/orders/${id}`, data)
  return res.data
}

export async function setFirstPaymentDate(id: number, first_payment_date: string): Promise<Order> {
  const res = await api.patch(`/api/v1/orders/${id}/first-payment-date`, { first_payment_date })
  return res.data
}

export async function activateOrder(id: number): Promise<Order> {
  const res = await api.post(`/api/v1/orders/${id}/activate`)
  return res.data
}

export async function getOrderPayments(orderId: number): Promise<Payment[]> {
  const res = await api.get(`/api/v1/orders/${orderId}/payments`)
  return res.data
}

export async function confirmPayment(
  orderId: number, paymentId: number, notes?: string, confirmed_amount?: number
): Promise<Payment> {
  const res = await api.post(`/api/v1/orders/${orderId}/payments/${paymentId}/confirm`, { notes, confirmed_amount })
  return res.data
}

export async function manualConfirmPayment(orderId: number, paymentId: number, amount?: number): Promise<Payment> {
  const res = await api.post(`/api/v1/orders/${orderId}/payments/${paymentId}/manual-confirm`, { confirmed_amount: amount ?? null })
  return res.data
}

export async function rejectPayment(
  orderId: number, paymentId: number, notes?: string
): Promise<Payment> {
  const res = await api.post(`/api/v1/orders/${orderId}/payments/${paymentId}/reject`, { notes })
  return res.data
}

export async function getAwaitingPayments(params?: {
  status?: string; due_from?: string; due_to?: string; offset?: number; limit?: number
}): Promise<Payment[]> {
  const res = await api.get('/api/v1/orders/awaiting-payments', { params })
  return res.data
}

export async function submitPayment(
  orderId: number,
  paymentId: number,
  opts: { method: 'cash' | 'transfer'; receipt_url?: string; paid_amount_uzs?: number }
): Promise<Payment> {
  const res = await api.post(
    `/api/v1/orders/${orderId}/payments/${paymentId}/submit`,
    null,
    { params: { method: opts.method, receipt_url: opts.receipt_url, paid_amount_uzs: opts.paid_amount_uzs } }
  )
  return res.data
}

export async function cancelOrder(id: number, reason: string): Promise<Order> {
  const res = await api.post(`/api/v1/orders/${id}/cancel`, { reason })
  return res.data
}

export async function deleteOrder(id: number, comment: string): Promise<{ detail: string; hard_delete: boolean }> {
  const res = await api.delete(`/api/v1/orders/${id}`, { data: { comment } })
  return res.data
}

export async function uploadReceipt(file: File): Promise<string> {
  const form = new FormData()
  form.append('file', file)
  const res = await api.post('/api/v1/upload/receipt', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return res.data.receipt_url
}
